import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useRef } from 'react';
import { cn } from '@/lib/utils';

const tabBaseClass =
  'whitespace-nowrap border-b-2 px-1 pb-2 text-[13px] font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-crimson-400/40 focus-visible:ring-offset-1';
const tabActiveClass = 'border-[#ac2f2f] text-slate-900';
const tabInactiveClass =
  'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700';

export interface HeaderTabItem {
  value: string;
  label: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
}

export interface HeaderTabsProps {
  items: HeaderTabItem[];
  value: string;
  onChange: (value: string) => void;
  /** Accessible name for the tablist. Defaults to "Tabs". */
  ariaLabel?: string;
  /**
   * When supplied, each tab gets `id={idBase}-tab-{value}` and
   * `aria-controls={idBase}-panel-{value}` so callers can wire up the matching
   * `role="tabpanel"` / `aria-labelledby` on their content.
   */
  idBase?: string;
  /** Non-tab content rendered after the tablist (e.g. an add button). */
  trailing?: ReactNode;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
}

/**
 * Underline-style tabs for in-page panel switching. Implements the WAI-ARIA
 * tabs pattern: a `role="tablist"` of `role="tab"` buttons with roving
 * tabindex and arrow / Home / End keyboard navigation (automatic activation).
 *
 * For tabs that navigate between routes, use {@link HeaderTabLinks} instead -
 * route links are a `<nav>`, not an ARIA tablist.
 */
export function HeaderTabs({
  items,
  value,
  onChange,
  ariaLabel = 'Tabs',
  idBase,
  trailing,
  className,
  tabClassName,
  activeTabClassName,
}: HeaderTabsProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeIndex = items.findIndex((item) => item.value === value);
  const tabStopIndex = activeIndex >= 0 ? activeIndex : items.findIndex((item) => !item.disabled);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const enabled = items.reduce<number[]>((acc, item, i) => {
      if (!item.disabled) acc.push(i);
      return acc;
    }, []);
    if (enabled.length === 0) return;

    const pos = enabled.indexOf(index);
    let nextPos: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextPos = (pos + 1) % enabled.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextPos = (pos - 1 + enabled.length) % enabled.length;
        break;
      case 'Home':
        nextPos = 0;
        break;
      case 'End':
        nextPos = enabled.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextIndex = enabled[nextPos];
    tabRefs.current[nextIndex]?.focus();
    onChange(items[nextIndex].value);
  };

  return (
    <div className={cn('-mb-px flex items-end gap-5', className)}>
      <div role="tablist" aria-label={ariaLabel} className="flex items-center gap-5">
        {items.map((item, index) => {
          const Icon = item.icon;
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={idBase ? `${idBase}-tab-${item.value}` : undefined}
              aria-selected={selected}
              aria-controls={idBase ? `${idBase}-panel-${item.value}` : undefined}
              disabled={item.disabled}
              tabIndex={index === tabStopIndex ? 0 : -1}
              onClick={() => onChange(item.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                tabBaseClass,
                Icon && 'flex items-center gap-1.5',
                selected ? tabActiveClass : tabInactiveClass,
                item.disabled && 'cursor-not-allowed opacity-40',
                tabClassName,
                selected && activeTabClassName,
              )}
            >
              {Icon && <Icon className="h-4 w-4" />}
              {item.label}
            </button>
          );
        })}
      </div>
      {trailing != null && (
        <div className="mb-0.5 flex items-center pb-2">{trailing}</div>
      )}
    </div>
  );
}

export interface HeaderTabLink {
  to: string;
  label: ReactNode;
  active: boolean;
  icon?: LucideIcon;
}

export interface HeaderTabLinksProps {
  items: HeaderTabLink[];
  /** Accessible name for the navigation. Defaults to "Tabs". */
  ariaLabel?: string;
  className?: string;
}

/**
 * Underline-style tabs that navigate between routes. Rendered as a `<nav>` of
 * links with `aria-current="page"` on the active item - the correct semantics
 * for routed navigation (there is no in-DOM tabpanel to associate, so this is
 * not an ARIA tablist).
 */
export function HeaderTabLinks({ items, ariaLabel = 'Tabs', className }: HeaderTabLinksProps) {
  return (
    <nav className={cn('-mb-px flex items-center gap-5', className)} aria-label={ariaLabel}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={item.active ? 'page' : undefined}
            className={cn(
              tabBaseClass,
              Icon && 'flex items-center gap-1.5',
              item.active ? tabActiveClass : tabInactiveClass,
            )}
          >
            {Icon && <Icon className="h-4 w-4" />}
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}
